import React, { Reducer, createContext, useReducer, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { fabric } from 'fabric';
import { useCardContext } from '../../context/card-context';
import { usePhoto } from '../../features/card-editor/hooks/use-photo';
import { useOpenImageEditDrawer } from '../../features/card-editor/hooks/use-open-image-edit-drawer';
import { cropAndRotateFormData } from '../../global-types';
import { CustomFabricObject } from '../../global-types/canvas';
import { useActiveCanvas } from '../../hooks';
import { useAddLowResIcon } from '../../hooks/use-add-low-res-icon';
import { useFeatureFlags } from '../../hooks/use-feature-flags';
import { useIsPodProductCode } from '../../hooks/use-is-pod-product-code';
import { useS3Upload } from '../../hooks/use-s3-upload';
import { setAngle, hideMiddleControls } from '../../utils';
import { getCardFaceClipPath } from '../../utils/canvas-utils/get-card-face-clip-path';
import { createClipPathRect } from '../../utils/canvas-utils/create-clip-path-rect';
import { bringEditableAreaBordersToFront } from '../../utils/canvas-utils/bring-editable-area-borders-to-front';
import { useAnalyticsContext } from '../analytics-context';
import { hideLoadingScreen, setIsSystemErrorOpen, showLoadingScreen, useAppContext } from '../app-context';
import { useInitializationDataContext } from '../data-context';
import { initState } from './crop-context-state';
import {
  CropContextState,
  CropAction,
  CropProviderProps,
  CreateContextProps,
  CropHistoryTransaction,
} from './crop-context-types';
import { captureCanvasSnapshot, HistorySnapshot } from '../../features/edit-history/edit-history';
import {
  applyClipPathToImage,
  createCroppingArea,
  getCropAndRotateFormData,
  getCropImagePosition,
  getCroppingAreaCoords,
} from './utils';
import {
  getOriginalDimensions,
  getCropAreaDimensions,
  scaleImageToOriginal,
  scaleCropArea,
  restoreOriginalScale,
} from './utils/scale-cropping-operations';
import { uploadCroppedImage } from './utils/upload-cropped-image';
import { resetRotationForCrop } from './utils/reset-rotation-for-crop';
import { setCustomControls } from '../../components/canvas-custom-config/utils/set-custom-controls';
import {
  deleteIcon,
  rotateIcon,
  scaleIcon,
  circleIcon,
  rectangleIcon,
} from '../../components/canvas-custom-config/utils/fabric-icons';

const CropContext = createContext<CreateContextProps>(undefined);

export const cropReducer: Reducer<CropContextState, CropAction> = (state, action) => {
  const { type } = action || { type: null };
  switch (type) {
    default: {
      throw new Error(`Unhandled action type in card-context`);
    }
  }
};

export const CropContextProvider = ({ children }: CropProviderProps) => {
  const { uploadFile } = useS3Upload();
  const [cropState, cropDispatch] = useReducer(cropReducer, initState);
  const { appDispatch } = useAppContext();
  const { cardState } = useCardContext();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const { fillPhotoZone } = usePhoto();
  const openImageEditDrawer = useOpenImageEditDrawer();
  const isPodProductCode = useIsPodProductCode();
  const { IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED } = useFeatureFlags();
  const { updateEditFormats } = useAnalyticsContext();
  const [currentImage, setCurrentImage] = useState<CustomFabricObject | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [isSavingCroppedImage, setIsSavingCroppedImage] = useState(false);
  const [originalImageState, setOriginalImageState] = useState<{
    top?: number;
    left?: number;
    scaleX?: number;
    scaleY?: number;
    angle?: number;
  } | null>(null);
  const croppingArea = useRef<fabric.Rect>();
  const canvas = useActiveCanvas() as { current: fabric.Canvas };
  const currentCardFace = cardState.cardFacesList[`${cardState.activeCardIndex}`];
  const isCroppingRef = useRef(false);
  const isFinishingCropRef = useRef(false);
  const cropSessionRef = useRef<{
    canvas: fabric.Canvas;
    before: HistorySnapshot;
    clipPath: fabric.Object | undefined;
  } | null>(null);

  const handleIconAdded = useAddLowResIcon();

  const cropDeselectionHandler = useCallback(() => {
    // Maintain cropping rectangle selected until cropping is finished.
    // `canvas` from `useActiveCanvas` is typed as defined here but can be
    // undefined when this callback was created during early renders before
    // the card faces finished loading; guard accordingly.
    if (isCroppingRef.current) {
      const croppingRect = croppingArea.current;
      const currentCanvas = canvas?.current;
      // A canvas reload can detach the rect; re-selecting it would leave an orphan active object.
      if (croppingRect && currentCanvas && croppingRect.canvas === currentCanvas) {
        currentCanvas.setActiveObject(croppingRect).renderAll();
      }
    }
  }, [canvas]);

  /**
   * Restores the selectable and envented values of the canvas objects as they are disabled during cropping
   */
  const restoreSelectableObjects = () => {
    const canvasObjects = canvas.current.getObjects();
    canvasObjects.forEach((obj) => {
      if (obj.data?.isSelectable) {
        obj.selectable = true;
      }
      if (obj.data?.isEvented) {
        obj.evented = true;
      }
    });
  };

  const startCropping = () => {
    const currentCanvas = canvas?.current;
    if (!currentCanvas) return;
    const currentImage = currentCanvas.getActiveObject() as CustomFabricObject;
    if (!currentImage) return;
    cropSessionRef.current = {
      canvas: currentCanvas,
      before: captureCanvasSnapshot(currentCanvas),
      clipPath: currentImage.clipPath,
    };
    if (currentImage.clipPath) {
      currentImage.clipPath = undefined;
    }

    // Save the original image state before cropping
    if (currentImage.top !== undefined && currentImage.left !== undefined) {
      setOriginalImageState({
        top: currentImage.top,
        left: currentImage.left,
        scaleX: currentImage.scaleX,
        scaleY: currentImage.scaleY,
        angle: currentImage.angle,
      });
    }

    isCroppingRef.current = true;
    createCroppingArea(
      currentImage,
      canvas,
      croppingArea,
      initializedData?.project_type_code,
      cropDeselectionHandler,
      IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED,
    );

    setCurrentImage(currentImage);
    setIsCropping(true);
  };

  const applyCrop = async () => {
    canvas.current?.off('selection:cleared', cropDeselectionHandler);

    if (
      croppingArea &&
      croppingArea.current &&
      currentImage &&
      currentImage.top !== undefined &&
      currentImage.left !== undefined
    ) {
      showLoadingScreen(appDispatch, 'Cropping your image...');

      // Temporarily reset image and crop rect to 0° for crop math.
      // Rendering is suppressed so the user never sees this.
      canvas.current.renderOnAddRemove = false;

      // Hoisted so they remain in scope after the try/finally block
      let currentAngle = 0;
      let cropDimensions: ReturnType<typeof getCropAreaDimensions>;
      let cropAndRotateFormData: cropAndRotateFormData;
      const croppedImage = new Image();

      try {
        resetRotationForCrop(currentImage, croppingArea.current);

        // Get original dimensions and crop area dimensions
        const originalDimensions = getOriginalDimensions(currentImage);
        cropDimensions = getCropAreaDimensions(croppingArea.current);

        // Scale image to original size
        scaleImageToOriginal(currentImage, originalDimensions);

        // Scale crop area proportionally
        scaleCropArea(croppingArea.current, originalDimensions, cropDimensions);

        // Set the angle and position of the crop area
        const { totalRotation, currentAngle: _currentAngle } = getCroppingAreaCoords({ croppingArea, currentImage });
        currentAngle = _currentAngle;
        (croppingArea.current as CustomFabricObject)._resetOrigin();
        setAngle(currentImage, totalRotation);

        // Get the position of image being cropped (now at original scale)
        const { xPos, yPos } = getCropImagePosition({ croppingArea, currentImage });

        // Only populate if values are not fractions of a pixel
        if (0 !== Math.round(xPos * 1000) && 0 !== Math.round(yPos * 1000)) {
          // Track when user is updating the image and where they are cropping it
          updateEditFormats({ crop: `x:${xPos}, y:${yPos}` });
        }

        // Generate the form data for cropping and rotating the image later
        cropAndRotateFormData = getCropAndRotateFormData({
          croppingArea: croppingArea,
          currentImage: currentImage,
          totalRotation,
          xPos,
          yPos,
        });

        // Creates a new image in memory
        croppedImage.src = currentImage.toDataURL({
          left: xPos,
          top: yPos,
          width: croppingArea.current.getScaledWidth(),
          height: croppingArea.current.getScaledHeight(),
        });

        // Restore original scales
        restoreOriginalScale(currentImage, croppingArea.current, originalDimensions, cropDimensions);

        // Restore objects selectable and evented original values
        restoreSelectableObjects();
      } finally {
        // Always restore rendering, even if an error occurs
        canvas.current.renderOnAddRemove = true;
      }

      if (!initializedData?.project_id || !currentImage.name) {
        throw Error('Unable to crop image: Project ID or Image Name is missing from application state');
      }

      return new Promise<fabric.Image>((resolve) => {
        fabric.Image.fromURL(
          croppedImage.src,
          (img) => {
            hideMiddleControls(img);
            const cardFaceClipPath = getCardFaceClipPath(currentCardFace, 0);
            img.crossOrigin = 'anonymous';
            if (cardFaceClipPath) {
              // Ensure image respects copyrighted zone boundaries with a fresh instance
              // to avoid shared-reference rendering issues across browsers
              img.clipPath = createClipPathRect(cardFaceClipPath);
            }

            // pod and photozone image
            if (isPodProductCode && currentImage.data?.photoZoneId) {
              img.data = {
                ...currentImage.data,
                positionOverrides: croppingArea.current
                  ? { top: croppingArea.current.top, left: croppingArea.current.left }
                  : undefined,
              };
              img.name = currentImage.name;
              canvas.current.remove(currentImage);
              canvas.current.remove(croppingArea.current as fabric.Object);
              croppingArea.current = undefined;
              fillPhotoZone({ image: img, photozoneId: currentImage.data.photoZoneId, onIconAdded: handleIconAdded });
              setAngle(img, currentAngle);
              setCurrentImage(null);
              isCroppingRef.current = false;
              setIsCropping(false);
              hideLoadingScreen(appDispatch);
              return resolve(img);
            }

            // For non-photozone images, maintain existing behavior
            img.scaleToWidth(cropDimensions.cropWidth);
            if (currentImage.data?.clipPathSettings && !currentImage?.data?.isPodHandwriting) {
              setCustomControls(
                { _fabric: window.fabric },
                { deleteIcon, rotateIcon, scaleIcon, circleIcon, rectangleIcon },
                appDispatch,
                false,
                IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED,
              );
              const clipPath = new fabric.Rect(currentImage.data.clipPathSettings);
              img.top = (clipPath.top as number) - img.getScaledHeight() / 2;
              img.left = (clipPath.left as number) - img.getScaledWidth() / 2;
              img.clipPath = clipPath;
            } else if (croppingArea.current) {
              // Store cropping position
              const targetTop = croppingArea.current.top;
              const targetLeft = croppingArea.current.left;

              // Apply original scale if available (maintaining aspect ratio of the cropped image)
              if (originalImageState && originalImageState.scaleX !== undefined && img.scaleX !== undefined) {
                const scaleRatio = originalImageState.scaleX / img.scaleX;
                img.scale(img.scaleX * scaleRatio);
              }

              img.top = targetTop;
              img.left = targetLeft;

              // Apply original angle if available
              if (originalImageState && originalImageState.angle !== undefined) {
                setAngle(img, originalImageState.angle);
              }
            }
            img.data = {
              ...currentImage.data,
              version_id: '',
            };
            img.name = currentImage.name;

            img.onSelect = () => {
              openImageEditDrawer();
              return false;
            };
            canvas?.current?.add(img);
            canvas?.current?.setActiveObject(img);
            bringEditableAreaBordersToFront(canvas?.current);
            // Remove cropping rectangle
            canvas.current.remove(croppingArea.current as fabric.Object);
            croppingArea.current = undefined;
            canvas.current.remove(currentImage);
            setCurrentImage(null);
            isCroppingRef.current = false;
            setIsCropping(false);
            setOriginalImageState(null); // Reset original image state
            hideLoadingScreen(appDispatch);
            resolve(img);
          },
          { crossOrigin: 'anonymous' },
        );
      })
        .then((fabricImage) => {
          if (!initializedData?.project_id || !fabricImage.name) {
            setIsSystemErrorOpen(appDispatch, true);
            throw Error('Unable to upload cropped image: Project ID or Image Name is missing');
          }

          setIsSavingCroppedImage(true);

          return uploadCroppedImage({
            uploadFile,
            croppedImageUrl: croppedImage.src,
            cropAndRotateFormData: cropAndRotateFormData,
            imageName: fabricImage.name,
            projectId: initializedData?.project_id,
            onUploadSuccess: (imageUrl) => {
              if (imageUrl) {
                fabricImage.data.croppedUrl = imageUrl;
                setIsSavingCroppedImage(false);
              }
            },
            onUploadError: (error) => {
              setIsSystemErrorOpen(appDispatch, true);
              setIsSavingCroppedImage(false);
              throw error;
            },
          });
        })
        .catch((error) => {
          setIsSystemErrorOpen(appDispatch, true);
          throw Error(error);
        });
    }
  };

  const finishCropping = async (runTransaction?: CropHistoryTransaction): Promise<void> => {
    if (isFinishingCropRef.current) {
      throw new Error('Crop is already being saved');
    }
    isFinishingCropRef.current = true;
    const session = cropSessionRef.current;
    try {
      if (runTransaction) {
        if (!session) {
          throw new Error('Cannot finish cropping without an active crop session');
        }
        await runTransaction(session.canvas, applyCrop, session.before);
      } else {
        await applyCrop();
      }
    } finally {
      canvas.current?.off('selection:cleared', cropDeselectionHandler);
      croppingArea.current = undefined;
      cropSessionRef.current = null;
      isFinishingCropRef.current = false;
      isCroppingRef.current = false;
      setCurrentImage(null);
      setOriginalImageState(null);
      setIsCropping(false);
      setIsSavingCroppedImage(false);
      hideLoadingScreen(appDispatch);
    }
  };

  const cancelCropping = () => {
    canvas.current?.off('selection:cleared', cropDeselectionHandler);
    if (croppingArea.current && currentImage) {
      canvas.current.remove(croppingArea.current);

      if (currentCardFace) {
        const cardFaceClipPath = getCardFaceClipPath(currentCardFace, 0);
        if (cardFaceClipPath) {
          // Ensure image respects copyrighted zone boundaries with a fresh instance
          currentImage.clipPath = createClipPathRect(cardFaceClipPath);
        }
      }

      applyClipPathToImage(currentImage);
      if (cropSessionRef.current) {
        currentImage.clipPath = cropSessionRef.current.clipPath;
      }
      setOriginalImageState(null);
      canvas.current.setActiveObject(currentImage);
      bringEditableAreaBordersToFront(canvas.current);
      setCurrentImage(null);
      isCroppingRef.current = false;
      setIsCropping(false);
      restoreSelectableObjects();
      canvas.current.requestRenderAll();
    }
    croppingArea.current = undefined;
    cropSessionRef.current = null;
  };

  useEffect(() => {
    if (isCropping && !croppingArea.current) {
      createCroppingArea(
        currentImage,
        canvas,
        croppingArea,
        initializedData?.project_type_code,
        cropDeselectionHandler,
        IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED,
      );
    }
  }, [isCropping]);

  return (
    <CropContext.Provider
      value={{
        cropDispatch,
        cropState,
        startCropping,
        finishCropping,
        isSavingCroppedImage,
        isCropping,
        cancelCropping,
      }}
    >
      {children}
    </CropContext.Provider>
  );
};

// Hooks
export const useCropContext = () => {
  const context = useContext(CropContext);
  if (context === undefined) {
    throw new Error('useCropContext must be used within CropProvider');
  }

  return context;
};
